import { 
    ActionPattern,
    actionChannel,
    call,
    debounce,
    fork,
    select,
    take,
    takeEvery
} from 'redux-saga/effects';
import {
    websocketCloseSubscriptionById,
    websocketOpenSubscriptionById,
    websocketChannelClose,
    websocketChannelOpen,
    websocketChannelSend,
    websocketEventClose,
    websocketEventMessage,
    websocketEventOpen,
    websocketTradingViewSubscribeAction,
    websocketTradingViewUnsubscribeAction,
    websocketEventError,
    websocketChannelUpdated,
} from '../websocketReducer';
import {
    onOpenSubscriptionByIdSaga,
    processSubscriptionByIdSaga,
    onCloseSubscriptionByIdSaga,
    onWebsocketOpenSaga,
    onWebsocketCloseSaga,
    onVisibilityChangeSaga,
    onLogoutStartSaga,
} from './websocketSubscriptionSagas';
import {
    onOpenChannelSaga,
    onCloseChannelSaga,
    onSendChannelSaga,
    onWebSocketCloseEventSaga,
    onWebSocketOpenEventSaga,
    onWebsocketChannelReadyStateChangeSagas,
} from './websocketChannelsSagas';

import {
    APP_INIT_SUCCESS,
    SET_USER_SELECTED_ACCOUNT_CODE,
    UPDATE_ACCOUNT_ACTION,
    USER_UPDATE_SUCCESS,
} from '../../../app/appConstants';
import {
    ADD_MARKET_DATA_CANDLE_WATCH_ACTION,
    ADD_MARKET_DATA_WATCH_ACTION,
    INIT_LITE_TRADE_SUCCESS,
    LOGIN_LITE_TRADE_SUCCESS,
    REMOVE_MARKET_DATA_CANDLE_WATCH_ACTION,
    REMOVE_MARKET_DATA_WATCH_ACTION,
} from '../../../modules/LiteTrade/liteTradeConstants';

import subscriptionAccountPortfoliosSaga from './subscribers/subscriptionAccountPortfoliosSagas';
import subscriptionMarketDataQuoteSaga from './subscribers/subscriptionMarketDataQuoteSagas';
import subscriptionMarketDataCandleSaga, {
    subscribeSymbolsDataWithCallbackSaga,
    unsubscribeTradingViewData,
} from './subscribers/subscriptionMarketDataCandleSagas';
import { Action } from 'redux';
import { waitForSaga } from '../helpers';
import { 
    initiationAccountEventsSelector, 
    initiationAccountMetricsSelector, 
    initiationAccountPortfoliosSelector,
    initiationConditionMarketDataQuoteSelector,
    initiationLiteTradeAssetSelector,
    initiationSymbolsDataWithCallbackSelector 
} from '../websocketSelectors';
import subscriptionAccountEventsSaga from './subscribers/subscriptionAccountEventsSagas';
import subscriptionAccountMetricsSaga from './subscribers/subscriptionAccountMetricsSagas';
import { onLiteTradeWSConnectorSaga } from './liteTradeWSConnectorSaga';
import { getLiteTradeAssetsSaga, setSelectedAccountSaga } from './liteTradeWssEventsSaga';
import { LOGOUT_START } from '../../../modules/Login/loginConstants';
import { INTERNET_CONNECTION_CHANGE } from '../../../modules/InternetConnection/internetConnectionConstants';


// утилиты для работы с каналами
// каналы (последовательные очереди событий)
export function* makeSequenceSaga(conditions: ActionPattern, worker: (action: Action) => any) {
    const sequenceChanel = yield actionChannel(conditions);
    while (true) {
        const action = yield take(sequenceChanel);
        yield call(worker, action);
    }
}

export function* withInitiationSaga(initiation, paramsSelector, sensor, worker) {
    // ждем когда инициализация будет выполнена
    yield call(waitForSaga, initiation);
    const params = yield select(paramsSelector);
    // выполняем работу
    yield call(worker, params);
    // отрабатываем нужные события последовательно в порядке очереди
    yield fork(makeSequenceSaga, sensor, worker);
}

export function* withInitiationDebounceSaga(initiation, paramsSelector, sensor, worker, debounceTime = 1000) {
    // ждем когда инициализация будет выполнена
    yield call(waitForSaga, initiation);
    const params = yield select(paramsSelector);
    // выполняем работу
    yield call(worker, params);
    yield debounce(debounceTime, sensor, worker);
}

export function* websocketSagas() {
    // работа с каналами
    yield takeEvery(websocketChannelOpen, onOpenChannelSaga);
    yield takeEvery(websocketChannelClose, onCloseChannelSaga);
    yield fork(makeSequenceSaga, websocketChannelSend, onSendChannelSaga);

    yield takeEvery([
        websocketEventClose,
        websocketEventError
    ], onWebSocketCloseEventSaga);
    yield takeEvery(websocketEventOpen, onWebSocketOpenEventSaga);
    yield takeEvery([
        'VISIBILITY_CHANGE',
        INTERNET_CONNECTION_CHANGE
    ], onVisibilityChangeSaga);

    // работа с подписками
    yield fork(makeSequenceSaga, websocketOpenSubscriptionById, onOpenSubscriptionByIdSaga);
    yield takeEvery(websocketEventMessage, processSubscriptionByIdSaga);
    yield takeEvery(websocketCloseSubscriptionById, onCloseSubscriptionByIdSaga);
    yield takeEvery(websocketEventOpen, onWebsocketOpenSaga);
    yield takeEvery(websocketEventClose, onWebsocketCloseSaga);

    // подписка на события
    yield takeEvery(LOGOUT_START, onLogoutStartSaga);

    yield takeEvery(
        [
            // getUserAccounts
            UPDATE_ACCOUNT_ACTION,
            APP_INIT_SUCCESS,
            USER_UPDATE_SUCCESS,
            LOGIN_LITE_TRADE_SUCCESS,
        ],
        setSelectedAccountSaga,
    );

    yield fork(
        withInitiationSaga,
        initiationAccountEventsSelector,
        () => true,
        [
            // selectedAccountCode
            SET_USER_SELECTED_ACCOUNT_CODE,
            LOGIN_LITE_TRADE_SUCCESS,
        ],
        subscriptionAccountEventsSaga,
    );
    yield fork(
        withInitiationSaga, 
        initiationLiteTradeAssetSelector, 
        () => true, 
        [
            APP_INIT_SUCCESS,
            LOGIN_LITE_TRADE_SUCCESS,
        ], 
        getLiteTradeAssetsSaga
    );
    yield fork(
        withInitiationSaga,
        initiationAccountMetricsSelector,
        () => true,
        [
            // getUserAccounts
            UPDATE_ACCOUNT_ACTION,
            APP_INIT_SUCCESS,
            USER_UPDATE_SUCCESS,
            LOGIN_LITE_TRADE_SUCCESS,
        ],
        subscriptionAccountMetricsSaga,
    );
    yield fork(
        withInitiationSaga,
        initiationAccountPortfoliosSelector,
        () => true,
        [
            UPDATE_ACCOUNT_ACTION, 
            USER_UPDATE_SUCCESS,
            APP_INIT_SUCCESS,
            LOGIN_LITE_TRADE_SUCCESS, 
        ],
        subscriptionAccountPortfoliosSaga,
    );
    yield takeEvery(websocketTradingViewSubscribeAction, subscriptionMarketDataCandleSaga);
    yield takeEvery(websocketTradingViewUnsubscribeAction, unsubscribeTradingViewData);

    yield fork(
        withInitiationDebounceSaga,
        initiationSymbolsDataWithCallbackSelector,
        () => true,
        [
            ADD_MARKET_DATA_CANDLE_WATCH_ACTION, 
            REMOVE_MARKET_DATA_CANDLE_WATCH_ACTION, 
            SET_USER_SELECTED_ACCOUNT_CODE,
            LOGIN_LITE_TRADE_SUCCESS,
        ],
        subscribeSymbolsDataWithCallbackSaga,
        // TODO Временно, после улучшение процесса загрузки или убрать или уменьшить время
        300
    );

    yield fork(
        withInitiationSaga,
        initiationConditionMarketDataQuoteSelector,
        () => true,
        [
            ADD_MARKET_DATA_WATCH_ACTION,
            REMOVE_MARKET_DATA_WATCH_ACTION,
            LOGIN_LITE_TRADE_SUCCESS,
        ],
        subscriptionMarketDataQuoteSaga,
    );

    yield takeEvery(
        [websocketEventClose, websocketEventOpen, websocketEventError, 'VISIBILITY_CHANGE', INIT_LITE_TRADE_SUCCESS],
        onLiteTradeWSConnectorSaga,
    );

    yield takeEvery([
        websocketEventClose,
        websocketEventOpen,
        websocketEventError,
        websocketChannelUpdated,
        'VISIBILITY_CHANGE',
        INIT_LITE_TRADE_SUCCESS,
        websocketChannelSend,
        websocketChannelClose,
        websocketChannelOpen,
    ], onWebsocketChannelReadyStateChangeSagas);
}
